import _ from 'underscore';
import React, { useState, useMemo, useCallback } from 'react';
import { Panel } from 'react-bootstrap';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withThemeFromProps } from '@bingads-webui-react/with-theme-from-props';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { withValidation, withError, GenericAdapter } from '@bingads-webui-react/validation';
import { Input, ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { Button, Label } from '@bingads-webui-react/primitive';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { Alert } from '@bingads-webui-react/alert';
import { PromotionAdExtension } from '@bingads-webui-campaign/campaign-ui-schemas';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { UpgradeUrlStringMap } from '@bingads-webui-campaign/upgrade-url-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { UrlEditorForArray } from '@bingads-webui-campaign-react/url-editor';
import {
  LanguageSelect as LanguageSelectPure,
  FinalUrlSuffix,
  TrackingTemplate,
  CustomParameters,
  buildSchedulingView,
  stringifyUnexpectedError,
  ErrorsContext,
  Errors,
  and, or, propertyIs, propertySuffixIs, isSchedulingError, unmappedError,
} from '@bingads-webui-campaign/ad-extensions-shared';
import { getODataErrors } from '@bingads-webui-campaign/api-errors';
import { OccasionSelect as OccasionSelectPure } from './controls/occasion-select';
import { CurrencySelect as CurrencySelectPure } from './controls/currency-select';
import { PromotionType } from './controls/promotion-type';
import { getPromotionExtensionEditorStyles } from './promotion-extension-editor-style';
import { PromotionDetail } from './controls/promotion-detail';
import { StartAndEndDateWithValidation } from './controls/start-and-end-date';
import { isCurrencyRequired } from './rules/currency';

const GENERIC_ERROR = _TL_("We've encountered an error and can't continue. Please contact support for further assistance.");

const OccasionSelect = withError(withValidation(OccasionSelectPure, GenericAdapter));
const LanguageSelect = withError(withValidation(LanguageSelectPure, GenericAdapter));
const CurrencySelect = withError(withValidation(CurrencySelectPure, GenericAdapter));

const whitelistedSchemaPropertyKeys = ['Id', 'Language', 'Occasion', 'DiscountModifier', 'MoneyAmountOff', 'PercentOff', 'OrdersOverAmount', 'PromotionCode', 'PromotionItem', 'PromotionStartDate', 'PromotionEndDate', 'FinalMobileUrls', 'FinalUrls', 'FinalUrlSuffix', 'TrackingUrlTemplate', 'UrlCustomParameters', 'Scheduling'];

function cleanModel(rawModel) {
  const stage1 = _.pick(rawModel, [
    ...whitelistedSchemaPropertyKeys,
    ...(isCurrencyRequired(rawModel) ? ['CurrencyCode'] : []),
  ]);
  // Cleaning UrlCustomParameters of initial item added by validation
  // where both Key and Value are undefined.
  const customParameters = _.filter(
    stage1.UrlCustomParameters,
    item => !_.isEmpty(item.Key) || !_.isEmpty(item.Value)
  );
  return _.extend({}, stage1, { UrlCustomParameters: customParameters });
}

export const PromotionExtensionEditor = ({
  classes,
  extension,
  i18n,
  legacyI18n: adExtI18n, // passed from legacy-ui, if needed
  data: {
    extension: repository,
  },
  instrumentation,
  onCreated,
  onUpdated,
  onCancel,
  permissions,
  deps: {
    SchedulingView: LegacySchedulingView,
  },
}) => {
  const isEditing = extension && !!extension.Id;

  const { scenario } = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName: 'PromotionAdExtensions',
    activityName: isEditing ? 'EditAdExtension' : 'CreateAdExtension',
  }), [instrumentation, isEditing]);

  const [isBlocking, setIsBlocking] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [formErrors, setFormErrors] = useState([]);

  const localLegacyI18n = useMemo(() =>
    (buildLegacyI18n(i18n, [ValidationStringMap, UpgradeUrlStringMap])), [i18n]);
  const SchedulingView =
    useMemo(() => buildSchedulingView(LegacySchedulingView), [LegacySchedulingView]);

  const { IsDomesticSimplifiedChineseEnabled } = permissions;

  const initialData = cleanModel({
    Language: IsDomesticSimplifiedChineseEnabled ? 'SimplifiedChinese' : 'English',
    Occasion: 'None',
    DiscountModifier: 'None',
    MoneyAmountOff: null,
    PercentOff: null,
    PromotionCode: null,
    OrdersOverAmount: null,
    FinalMobileUrls: [],
    FinalUrls: [],
    FinalAppUrls: [],
    UrlCustomParameters: [],
    ...extension,
  }, PromotionAdExtension);

  const urlOptionsExpandedInitially = !!(extension &&
    (extension.TrackingUrlTemplate ||
      extension.FinalUrlSuffix ||
      !_.isEmpty(extension.UrlCustomParameters)));

  const [data, setData] = useState(initialData);
  const [prevData, setPrevData] = useState(initialData);
  const [dirtyFieldsMap, setDirtyFieldsMap] = useState({});
  const [isFinalUrlSuffixValid, setIsFinalUrlSuffixValid] = useState(true);
  const [isCustomParametersValid, setIsCustomParametersValid] = useState(true);
  const [isScheduleValid, setIsScheduleValid] = useState(initialData);

  const hasNoChanges = useCallback(prop => () => !dirtyFieldsMap[prop], [dirtyFieldsMap]);

  const resetIsDirty = useCallback(() => {
    setDirtyFieldsMap({});
  }, []);

  const registerDataChange = useCallback((newData) => {
    _.each(newData, (value, key) => {
      if (key !== 'url' && !_.isEqual(_.result(prevData, key, null), _.result(newData, key, null))) {
        setDirtyFieldsMap({
          ...dirtyFieldsMap,
          [key]: true,
        });
      }
    });
  }, [prevData, dirtyFieldsMap]);

  const isCurrencyVisible = isCurrencyRequired(data);
  // If we for the first time enable currency field,
  // set the CurrencyCode to the default value
  if (isCurrencyVisible && !data.CurrencyCode) {
    setData(currentData => ({ ...currentData, CurrencyCode: 'USD' }));
  }

  const onValidate = useCallback(({ isValid: newIsValid, data: newData }) => {
    setIsValid(newIsValid);
    setData(currentData => ({ ...currentData, ...newData }));
  }, []);

  const onDataChange = useCallback((newData) => {
    registerDataChange(newData);
    setData(currentData => ({ ...currentData, ...newData }));
  }, [registerDataChange]);

  const onCustomParametersChange = useCallback(({ isValid: newIsValid, value }) => {
    setIsCustomParametersValid(newIsValid);
    onDataChange({ UrlCustomParameters: value });
  }, [onDataChange]);

  const onFinalUrlSuffixChange = useCallback(({ isValid: newIsValid, value }) => {
    setIsFinalUrlSuffixValid(newIsValid);
    onDataChange({ FinalUrlSuffix: value });
  }, [onDataChange]);

  const onScheduleChange = useCallback(({ isValid: newIsValid, value }) => {
    setIsScheduleValid(newIsValid);
    onDataChange({ Scheduling: value });
  }, [onDataChange]);

  const onSaveClicked = useCallback(() => {
    const saveActivity = scenario.createActivity('SaveAdExtension');
    saveActivity.start();

    setPrevData(data);

    setIsBlocking(true);
    repository
      .create(cleanModel(data))
      .then((newModel) => {
        saveActivity.stop(true);
        const callback = isEditing ? onUpdated : onCreated;
        callback(cleanModel(newModel));
      }, (errors) => {
        saveActivity.stop(false);
        let newFormErrors;
        if (_.isArray(errors.errors)) {
          newFormErrors = getODataErrors(errors.errors, i18n);
        } else if (errors instanceof repository.BadResponseError) {
          newFormErrors = [errors];
        } else if (_.isArray(errors)) {
          newFormErrors = errors;
        } else {
          saveActivity.error(`Unexpected error: ${stringifyUnexpectedError(errors)}`);
          newFormErrors = [i18n.getString(GENERIC_ERROR)];
        }
        setFormErrors(newFormErrors);
      })
      .finally(() => {
        resetIsDirty();
        setIsBlocking(false);
      });
  }, [data, i18n, isEditing, onCreated, onUpdated, repository, scenario, resetIsDirty]);

  const { IsFinalURLSuffixPhase4Enabled } = permissions;

  /* eslint-disable max-len */
  return (
    <div className={classnames('promotion-extension-editor', 'extension-editor__container')} data-type="PromotionExtension">
      <UiBlocker blocking={isBlocking} loadingText={i18n.getString(_TL_('Loading...'))}>
        <ErrorsContext.Provider value={formErrors}>
          <div
            className={classnames(
              'promotion-extension-editor',
              classes.outerGridWithOneColumn
            )}
          >
            <div
              className={classnames(
                classes.editorFieldsColumn,
                classes.outerGridFlexibleGridRow1,
                classes.fieldsGrid
              )}
            >
              <ValidationGroup
                validationProps={{
                  schema: PromotionAdExtension,
                  i18n: localLegacyI18n,
                  data,
                  onValidate,
                }}
              >
                <div className={classnames(classes.fieldsGridFlexibleGridRow1, classes.editorFieldLabel)}>
                  <Label htmlFor="occasion">{i18n.getString(_TL_('Occasion'))}</Label>
                  <HelpIcon topic="pop_BA_PromoExtension_Occasion" />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow2, classes.editorField)}>
                  <OccasionSelect
                    id="occasion"
                    className={classnames(classes.occasionSelect, 'promotion-extension-editor__occasion')}
                    validationProps={{
                      field: 'Occasion',
                    }}
                    i18n={i18n}
                  />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow3, classes.editorFieldNoMargin, classes.languageAndCurrencyGrid)}>
                  <div className={classnames(classes.languageAndCurrencyGridFlexibleGridRow1, classes.editorFieldLabel)}>
                    <Label htmlFor="language">{i18n.getString(_TL_('Language'))}</Label>
                    <HelpIcon topic="pop_BA_PromoExtension_Language" />
                  </div>
                  <div className={classnames(classes.languageAndCurrencyGridFlexibleGridRow2, classes.editorField)}>
                    <LanguageSelect
                      id="language"
                      className="promotion-extension-editor__language"
                      validationProps={{
                        field: 'Language',
                      }}
                      i18n={i18n}
                      permissions={permissions}
                    />
                  </div>
                  {isCurrencyVisible &&
                    <div className={classnames(classes.languageAndCurrencyGridFlexibleGridRow1, classes.currencyFieldLabel)}>
                      <Label htmlFor="currency">{i18n.getString(_TL_('Currency'))}</Label>
                      <HelpIcon topic="pop_BA_PromoExtension_Currency" />
                    </div>
                  }
                  {isCurrencyVisible &&
                    <div className={classnames(classes.languageAndCurrencyGridFlexibleGridRow2, classes.currencyEditorField)}>
                      <CurrencySelect
                        id="currency"
                        className="promotion-extension-editor__currency"
                        validationProps={{
                          field: 'CurrencyCode',
                        }}
                        i18n={i18n}
                        permissions={permissions}
                      />
                    </div>
                  }
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow4, classes.editorFieldLabel)}>
                  <Label htmlFor="start-and-end-date">{i18n.getString(_TL_('Displayed start date - Displayed end date'))}</Label>
                  <HelpIcon topic="pop_BA_PromoExtension_DisplayStartAndEndDate" />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow5, classes.editorField)}>
                  <div className={classes.alert} >
                    <Alert
                      message={i18n.getString(_TL_('Please ensure that the <b>Displayed start date – Displayed end date</b> matches your promotion extension’s <b>Start date</b> and <b>End date</b> below <b>Selected days and hours</b>.'))}
                      level="Info"
                      icon={(<div className="alert-image" />)}
                      dismissible={false}
                    />
                  </div>
                  <StartAndEndDateWithValidation
                    id="start-and-end-date"
                    className={classnames(classes.startAndEndDateContainer)}
                    i18n={i18n}
                    value={_.pick(data, 'PromotionStartDate', 'PromotionEndDate')}
                    onChange={onDataChange}
                    startDateClassName={classes.startAndEndDatePicker}
                    endDateClassName={classes.startAndEndDatePicker}
                    startAndEndDateSeparator={classes.startAndEndDateSeparator}
                  />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow6, classes.editorFieldLabel)}>
                  <Label htmlFor="promotion-item">{i18n.getString(_TL_('Item'))}</Label>
                  <HelpIcon topic="pop_BA_PromoExtension_Item" />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow7, classes.editorField)}>
                  <Input
                    id="promotion-item"
                    validationProps={{
                      field: 'PromotionItem',
                    }}
                  />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow8, classes.editorFieldLabel)}>
                  <Label htmlFor="final-url">{i18n.getString(_TL_('Final URL'))}</Label>
                  <HelpIcon topic="pop_BA_PromoExtension_FinalURL" />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow9, classes.editorField)}>
                  <Errors filter={and(hasNoChanges('FinalUrls'), or(propertySuffixIs('FinalUrls'), propertyIs('FinalUrls')))}>
                    <UrlEditorForArray
                      id="final-url"
                      field="FinalUrls"
                      value={data}
                      schema={PromotionAdExtension}
                      onChange={onDataChange}
                      containerClassName={classnames(classes.urlContainer, 'final-url-editor')}
                      selectClassName={classes.urlProtocolSelect}
                    />
                  </Errors>
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow10, classes.editorFieldLabel)}>
                  <Label htmlFor="final-mobile-url">{i18n.getString(_TL_('Mobile URL'))}</Label>
                  <HelpIcon topic="pop_BA_PromoExtension_MobileURL" />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow11, classes.editorField)}>
                  <Errors filter={and(hasNoChanges('FinalMobileUrls'), or(propertyIs('FinalMobileUrls'), propertyIs('MobileFinalUrls')))}>
                    <UrlEditorForArray
                      id="final-mobile-url"
                      field="FinalMobileUrls"
                      value={data}
                      schema={PromotionAdExtension}
                      onChange={onDataChange}
                      containerClassName={classnames(classes.urlContainer, 'final-mobile-url-editor')}
                      selectClassName={classes.urlProtocolSelect}
                    />
                  </Errors>
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow12, classes.editorFieldLabel)}>
                  <Label htmlFor="promotion-type">{i18n.getString(_TL_('Promotion type'))}</Label>
                  <HelpIcon topic="pop_BA_PromoExtension_PromoType" />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow13, classes.editorField)}>
                  <PromotionType
                    id="promotion-type"
                    promotionTypeSelectClassName={classes.promotionTypeAndDetailSelect}
                    inputContainerClass={classes.typeInputContainer}
                    inputClass={classes.typeInput}
                    inputExtraClass={classes.typeInputExtraExtra}
                    i18n={i18n}
                    legacyI18n={localLegacyI18n}
                    value={_.pick(data, 'DiscountModifier', 'MoneyAmountOff', 'PercentOff', 'CurrencyCode')}
                    onChange={onDataChange}
                  />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow14, classes.editorFieldLabel)}>
                  <Label htmlFor="promotion-detail">{i18n.getString(_TL_('Promotion details'))}</Label>
                  <HelpIcon topic="pop_BA_PromoExtension_PromoDetails" />
                </div>
                <div className={classnames(classes.fieldsGridFlexibleGridRow15, classes.editorField)}>
                  <PromotionDetail
                    id="promotion-detail"
                    promotionDetailTypeSelectClassName={classes.promotionTypeAndDetailSelect}
                    inputContainerClass={classes.typeInputContainer}
                    inputClass={classes.typeInput}
                    inputExtraClass={classes.typeInputExtraExtra}
                    i18n={i18n}
                    legacyI18n={localLegacyI18n}
                    value={_.pick(data, 'PromotionCode', 'OrdersOverAmount', 'CurrencyCode')}
                    onChange={onDataChange}
                  />
                </div>
                <Panel
                  defaultExpanded={urlOptionsExpandedInitially}
                  className={classnames(classes.collapsiblePanel, 'url-options-panel')}
                >
                  <Panel.Heading>
                    <Panel.Title toggle className={classnames(classes.fieldsGridFlexibleGridRow16)}>
                      {i18n.getString(_TL_('URL options'))}
                    </Panel.Title>
                  </Panel.Heading>
                  <Panel.Collapse>
                    <Panel.Body>
                      <div className={classnames(classes.fieldsGridFlexibleGridRow17, classes.editorFieldLabel)}>
                        <Label htmlFor="tracking-url-template">{i18n.getString(_TL_('Tracking template'))}</Label>
                        <HelpIcon topic="pop_BA_NewCampaignCreate_Ad_TrackTemplate" />
                      </div>
                      <div className={classnames(classes.fieldsGridFlexibleGridRow18, classes.editorField)}>
                        <Errors filter={and(hasNoChanges('TrackingUrlTemplate'), propertyIs('TrackingUrlTemplate'))}>
                          <TrackingTemplate
                            id="tracking-url-template"
                            i18n={i18n}
                            validationProps={{
                              field: 'TrackingUrlTemplate',
                            }}
                          />
                        </Errors>
                      </div>
                      {IsFinalURLSuffixPhase4Enabled &&
                        <React.Fragment>
                          <div className={classnames(classes.fieldsGridFlexibleGridRow19, classes.editorFieldLabel)}>
                            <Label htmlFor="final-url-suffix">{i18n.getString(_TL_('Final URL suffix'))}</Label>
                            <HelpIcon topic="pop_BA_NewAdCreate_FinalURLSuffix" />
                          </div>
                          <div className={classnames(classes.fieldsGridFlexibleGridRow20, classes.editorField)}>
                            <Errors filter={and(hasNoChanges('FinalUrlSuffix'), propertyIs('FinalUrlSuffix'))}>
                              <FinalUrlSuffix
                                id="final-url-suffix"
                                legacyI18n={localLegacyI18n}
                                value={data.FinalUrlSuffix}
                                onChange={onFinalUrlSuffixChange}
                              />
                            </Errors>
                          </div>
                        </React.Fragment>
                      }
                      <div className={classnames(classes.fieldsGridFlexibleGridRow21, classes.editorFieldLabel)}>
                        <Label htmlFor="custom-parameters">{i18n.getString(_TL_('Custom parameters'))}</Label>
                        <HelpIcon topic="pop_BA_NewCampaignCreate_Ad_Parameters" />
                      </div>
                      <div className={classnames(classes.fieldsGridFlexibleGridRow22, classes.editorFieldNoMargin)}>
                        <Errors filter={and(hasNoChanges('UrlCustomParameters'), propertyIs('UrlCustomParameters'))}>
                          <CustomParameters
                            id="custom-parameters"
                            onChange={onCustomParametersChange}
                            value={data.UrlCustomParameters}
                            legacyI18n={localLegacyI18n}
                            permissions={permissions}
                          />
                        </Errors>
                      </div>
                    </Panel.Body>
                  </Panel.Collapse>
                </Panel>
                <div className={classnames(classes.fieldsGridFlexibleGridRow23, classes.schedulingPanel)}>
                  <Errors filter={and(hasNoChanges('Scheduling'), isSchedulingError)}>
                    <SchedulingView
                      id="scheduling"
                      legacyI18n={localLegacyI18n}
                      permissions={permissions}
                      value={data.Scheduling}
                      onChange={onScheduleChange}
                    />
                  </Errors>
                </div>
              </ValidationGroup>
              <div className={classnames(classes.fieldsGridFlexibleGridRow24, classes.errors, 'save-errors')}>
                <Errors filter={unmappedError} />
              </div>
              <div className={classnames(classes.fieldsGridFlexibleGridRow25, classes.notices)} >
                {isEditing &&
                  <Alert
                    message={adExtI18n.getString('AdExtensions_Edit_Warning')}
                    level="Info"
                    icon={(<div className="alert-image" />)}
                    dismissible={false}
                  />
                }
                <Alert
                  message={adExtI18n.getString('AdExtensions_Created_Latency')}
                  level="Info"
                  icon={(<div className="alert-image" />)}
                  dismissible={false}
                />
              </div>
              <div className={classnames(classes.fieldsGridFlexibleGridRow26, classes.editorButtonGroup)} >
                <Button className={classnames('save', 'promotion_extension__save-button')} btnStyle="primary" bsStyle="primary" disabled={!isValid || !isCustomParametersValid || !isFinalUrlSuffixValid || !isScheduleValid} onClick={onSaveClicked}>{i18n.getString(_TL_('Save'))}</Button>
                <Button className={classnames('cancel', 'promotion_extension__cancel-button')} onClick={onCancel}>{i18n.getString(_TL_('Cancel'))}</Button>
              </div>
            </div>
          </div>
        </ErrorsContext.Provider>
      </UiBlocker>
    </div>
  );
  /* eslint-enable max-len */
};

PromotionExtensionEditor.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  onCreated: PropTypes.func.isRequired,
  onUpdated: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  extension: PropTypes.shape({
    Id: PropTypes.number,
  }),
  i18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
  legacyI18n: PropTypes.shape({ getString: PropTypes.func }).isRequired,
  data: PropTypes.shape({
    extension: PropTypes.shape({
      create: PropTypes.func,
    }),
  }).isRequired,
  instrumentation: PropTypes.shape({
    startScenario: PropTypes.func,
    startActivity: PropTypes.func,
    logMessage: PropTypes.func,
    endActivity: PropTypes.func,
    asLoggingStart: PropTypes.func,
  }),
  instrumentationSettings: PropTypes.shape({
    ACTIVITYNAME: PropTypes.shape({}),
    SCENARIONAME: PropTypes.shape({}),
  }),
  permissions: PropTypes.shape({
    IsDomesticSimplifiedChineseEnabled: PropTypes.bool,
  }).isRequired,
  deps: PropTypes.shape({
    SchedulingView: PropTypes.func.isRequired,
  }).isRequired,
};

PromotionExtensionEditor.defaultProps = {
  extension: null,
  instrumentation: {
    startScenario: _.noop,
    startActivity: _.noop,
    logMessage: _.noop,
    endActivity: _.noop,
    asLoggingStart: _.constant(_.noop),
  },
  instrumentationSettings: {
    ACTIVITYNAME: {},
    SCENARIONAME: {},
  },
};

export const StyledPromotionExtensionEditor = withThemeFromProps(withDefaultStyles(
  PromotionExtensionEditor,
  getPromotionExtensionEditorStyles
));
